import * as React from 'react';
import { motion } from 'framer-motion';
import type { MotionStyle } from 'framer-motion';
import clsx from 'clsx';
import type { Echo } from '@/types';
import { Badge } from './Badge';
import { Button } from '@/components/ui/Button';

import waveElement from '@/assets/images/svg/wave_element_echo_card.svg';
import starIcon from '@/assets/images/svg/rate_icon.svg';
import starFilledIcon from '@/assets/images/svg/star.svg';
import { getFullImageUrl } from '@/utils/url';
import { cn } from '@/utils/cn';

// Import Blur SVGs
import politicBlur from '@/assets/images/elements/politic_blur.svg';
import religionBlur from '@/assets/images/elements/religion_blur.svg';
import sportsBlur from '@/assets/images/elements/sports_blur.svg';

const ImageGradientOverlay: React.FC<{ isCreate: boolean }> = ({ isCreate }) => (
  <div
    className={clsx(
      'pointer-events-none absolute bottom-0 left-0 w-full',
      isCreate
        ? 'h-1/2 bg-gradient-to-t from-black/60 to-transparent'
        : 'h-2/5 bg-gradient-to-t from-brand-800 via-brand-800/50 to-transparent'
    )}
  />
);

interface TextOverlayProps {
  echo?: Echo | undefined;
  onAction?: (() => void) | undefined;
  onClick?: (() => void) | undefined;
}

const TextOverlayLayer: React.FC<TextOverlayProps> = ({ echo, onAction, onClick }) => (
  <div className='pointer-events-none absolute bottom-0 left-0 z-30 flex h-full w-3/5 flex-col justify-end pb-4 pl-4 xl:p-6'>
    <h2 className='mb-2 line-clamp-2 text-left font-proxima text-[18px] font-extrabold leading-105 tracking-normal text-white xl:text-[30px]'>
      {echo?.name}
    </h2>
    {echo?.bio && (
      <p className='line-clamp-4 text-left font-sans text-[12px] font-normal leading-[16px] tracking-normal text-white md:mb-5 xl:text-[15px] xl:leading-[20px]'>
        {echo.bio}
      </p>
    )}
    <Button
      type='button'
      variant='purple'
      className={clsx(
        'hidden xl:flex',
        'xl:pointer-events-auto',
        'h-[46px] w-[244px] rounded-[30px]',
        'bg-gradient-to-r from-purple-300 to-brand-420',
        'font-sans text-[20px] font-bold text-white',
        'transition-transform hover:scale-105 active:scale-95'
      )}
      onClick={(e) => {
        e.stopPropagation();
        if (onAction) onAction();
        else onClick?.();
      }}
    >
      Start Chatting
    </Button>
  </div>
);

interface CreatePanelProps {
  echoCount: number;
  actionLabel: string;
  onAction?: (() => void) | undefined;
}

const CreatePanel: React.FC<CreatePanelProps> = ({ echoCount, actionLabel, onAction }) => (
  <div
    className={clsx(
      'absolute bottom-0 left-0 z-30 flex w-full flex-col items-center',
      'pb-[7px] md:pb-4'
    )}
  >
    <span
      className={clsx(
        'm-0 p-0',
        'font-proxima font-normal',
        'text-[13px] md:text-20',
        'text-gray-text-muted',
        'mb-2 mt-sidebar-offset md:mb-[1.0625rem]',
        'leading-none'
      )}
    >
      {echoCount} Echoes Created
    </span>
    <Button
      variant='create-echo'
      className={clsx(
        'h-[23px] w-[129px] text-[10px]',
        'md:h-[2.0625rem] md:w-full md:max-w-[13.75rem] md:text-[14px]',
        'rounded-[30px] md:rounded-30',
        'opacity-100',
        'border-2',
        'px-0 md:px-4'
      )}
      onClick={(e) => {
        e.stopPropagation();
        onAction?.();
      }}
    >
      {actionLabel}
    </Button>
  </div>
);

function getBlurSrc(category: string): string | null {
  const lowerCat = category.toLowerCase();
  if (lowerCat.includes('politics')) return politicBlur;
  if (lowerCat.includes('religion')) return religionBlur;
  if (lowerCat.includes('sport') || lowerCat.includes('tennis') || lowerCat.includes('athlete')) {
    return sportsBlur;
  }
  return null;
}

interface CardVisualsProps {
  hideBadges: boolean;
  badgeContainerClassName?: string | undefined;
  fansCount: string;
  fansBadgeClassName?: string | undefined;
  isCreate: boolean;
  starsCount: string;
  activeStarIcon: string;
  starBadgeClassName?: string | undefined;
  waveClassName: string;
  blurSrc: string | null;
  blurClassName: string;
  glowClassName?: string | undefined;
  imageContainerBase: string;
  isHomeCard: boolean;
  imageContainerClassName?: string | undefined;
  finalImageSrc: string;
  name: string;
  imageClassName?: string | undefined;
  defaultImageClass: string;
  hidePanel: boolean;
  panelClassName?: string | undefined;
  panelVariants: Record<string, Record<string, string>>;
  titleClassName?: string | undefined;
  category: string;
  echoCount: number;
  actionLabel: string;
  onAction?: (() => void) | undefined;
}

const CardVisuals: React.FC<CardVisualsProps> = ({
  hideBadges,
  badgeContainerClassName,
  fansCount,
  fansBadgeClassName,
  isCreate,
  starsCount,
  activeStarIcon,
  starBadgeClassName,
  waveClassName,
  blurSrc,
  blurClassName,
  glowClassName,
  imageContainerBase,
  isHomeCard,
  imageContainerClassName,
  finalImageSrc,
  name,
  imageClassName,
  defaultImageClass,
  hidePanel,
  panelClassName,
  panelVariants,
  titleClassName,
  category,
  echoCount,
  actionLabel,
  onAction,
}) => (
  <>
    {!hideBadges && (
      <div
        className={clsx(
          'z-20 flex items-center',
          'justify-start gap-2 p-4',
          badgeContainerClassName
        )}
      >
        <Badge
          label={`${fansCount} Fans`}
          className={cn('!h-[29px] !w-[79px] !p-0 text-12', fansBadgeClassName)}
        />
        <Badge
          label={isCreate ? '0' : starsCount}
          variant='transparent'
          icon={<img src={activeStarIcon} alt='' className='mb-1 size-4' />}
          className={cn('text-12', starBadgeClassName || '!justify-end')}
        />
      </div>
    )}

    <div className='pointer-events-none absolute inset-0 z-0'>
      <img src={waveElement} alt='' className={waveClassName} />

      {blurSrc && !isCreate && (
        <img src={blurSrc} alt='' className={clsx(blurClassName, glowClassName)} />
      )}
    </div>

    <div
      className={clsx(
        imageContainerBase,
        isCreate ? '-mb-4 h-glow md:h-[300px] 3xl:h-[380px]' : '',
        !isHomeCard && !isCreate && 'h-[150px] md:h-[240px] 3xl:h-[320px]',
        imageContainerClassName
      )}
    >
      <img
        src={finalImageSrc}
        alt={name}
        className={clsx(imageClassName || defaultImageClass)}
        loading='lazy'
      />
      <ImageGradientOverlay isCreate={isCreate} />
    </div>

    {!isCreate && !hidePanel && (
      <motion.div
        className={clsx(
          'absolute bottom-0 left-0 z-30 hidden w-full flex-col items-center justify-start md:flex',
          'bg-glass-panel-gradient text-center',
          'h-[65px]',
          'pt-[0.8125rem]',
          panelClassName
        )}
        variants={panelVariants}
        transition={{ type: 'tween', ease: 'easeOut', duration: 0.3 }}
      >
        <h3
          className={clsx(
            'font-proxima font-bold text-white',
            'text-[22px] leading-105 tracking-normal',
            'mb-[2px]',
            titleClassName
          )}
        >
          {name}
        </h3>
        <p
          className={clsx(
            'font-sans font-semibold text-brand-800',
            'text-[14px] leading-27 tracking-normal',
            'mb-2'
          )}
        >
          {category}
        </p>
      </motion.div>
    )}

    {isCreate && (
      <CreatePanel echoCount={echoCount} actionLabel={actionLabel} onAction={onAction} />
    )}
  </>
);

interface EchoCardProps {
  echo?: Echo;
  imageSrc: string;
  borderColor?: string;
  className?: string;
  imageContainerClassName?: string;
  panelClassName?: string;
  titleClassName?: string;
  glowClassName?: string;
  badgeContainerClassName?: string;
  starBadgeClassName?: string;
  fansBadgeClassName?: string;
  imageClassName?: string;
  variant?: 'default' | 'create';
  onAction?: () => void;
  onClick?: () => void;
  echoCount?: number;
  actionLabel?: string;
  starsCount?: string;
  fansCount?: string;
  hideBadges?: boolean;
  disableHover?: boolean;
  hidePanel?: boolean;
  showTextOverlay?: boolean;
}

/**
 * Discoverable echo card — renders the avatar, name, category badges, and
 * hover-state interactions used across home, search, and contact-list rails.
 * Heavy on visual variants (panel/no-panel, hover/no-hover, badge subsets);
 * keep the boolean prop list flat for callers.
 */
export const EchoCard: React.FC<EchoCardProps> = ({
  echo,
  imageSrc,
  borderColor = '#BFB0F8',
  className,
  imageContainerClassName,
  panelClassName,
  titleClassName,
  glowClassName,
  badgeContainerClassName,
  starBadgeClassName,
  fansBadgeClassName,
  imageClassName,
  variant = 'default',
  onAction,
  onClick,
  echoCount = 0,
  actionLabel = 'CREATE ECHO',
  starsCount = '0',
  fansCount = '0',
  hideBadges = false,
  disableHover = false,
  hidePanel = false,
  showTextOverlay = false,
}) => {
  const isCreate = variant === 'create';
  // Use showTextOverlay as the flag for the new Home Page layout vs the Classic/Auth layout
  const isHomeCard = showTextOverlay;
  const name = echo?.name || 'My Echo';
  const category = echo?.category || '';

  const finalImageSrc = React.useMemo(() => getFullImageUrl(imageSrc) ?? '', [imageSrc]);

  const blurSrc = React.useMemo(() => getBlurSrc(category), [category]);

  const waveClassName = isHomeCard
    ? 'absolute bottom-0 right-0 h-[120%] w-2/3 opacity-100'
    : 'absolute bottom-[-2] right-0 h-[120%] opacity-100';

  const blurClassName = isHomeCard
    ? 'absolute -bottom-1/2 right-[-30%] w-full max-w-none translate-x-0 translate-y-0 md:left-1/2 md:top-[60%] md:w-auto md:max-w-[150%] md:-translate-x-1/2 md:-translate-y-1/2'
    : 'absolute left-1/2 top-[60%] max-w-[150%] -translate-x-1/2 -translate-y-1/2';

  const imageContainerBase = isHomeCard
    ? 'absolute bottom-0 right-0 z-10 flex h-full w-auto items-end justify-end'
    : 'absolute bottom-0 left-1/2 z-10 flex w-full -translate-x-1/2 items-end justify-center';

  const defaultImageClass = isHomeCard
    ? 'h-full w-auto object-cover'
    : 'size-full object-cover object-top';

  const cardVariants = isCreate
    ? {}
    : {
        idle: { borderColor },
        hover: { borderColor },
      };

  const styleMotion: MotionStyle = isCreate ? {} : ({ borderColor } as MotionStyle);

  // Logic: Show filled star if starsCount is anything other than "0" or "0K"
  const showFilledStar = starsCount !== '0' && starsCount !== '0K';
  const activeStarIcon = showFilledStar ? starFilledIcon : starIcon;

  // Determine interaction state
  // If disabled, we force 'hover' state so the panel is always visible
  const initialVariant = disableHover ? 'hover' : 'idle';
  const animateVariant = disableHover ? 'hover' : 'idle';

  const panelVariants = {
    idle: { y: '100%' },
    hover: { y: '0%' },
  };

  return (
    <div className='group relative flex w-full flex-col'>
      <motion.div
        className={cn(
          'relative flex shrink-0 flex-col',
          onClick ? 'cursor-pointer' : 'cursor-default',
          'overflow-hidden',
          'rounded-20',
          'w-full',
          !isCreate && 'max-w-[563px] border-4',
          isCreate && 'border-3',

          isHomeCard && 'min-h-[209px]',
          isHomeCard && 'xl:pointer-events-none',

          !isHomeCard && 'aspect-[163/186] md:aspect-auto',

          isCreate ? 'border-accent-glow bg-my-echo-gradient' : 'bg-brand-800',
          className
        )}
        style={styleMotion}
        onClick={onClick}
        initial={initialVariant}
        animate={animateVariant}
        {...(!disableHover ? { whileHover: 'hover' as const } : {})}
        variants={cardVariants}
      >
        <CardVisuals
          hideBadges={hideBadges}
          badgeContainerClassName={badgeContainerClassName}
          fansCount={fansCount}
          fansBadgeClassName={fansBadgeClassName}
          isCreate={isCreate}
          starsCount={starsCount}
          activeStarIcon={activeStarIcon}
          starBadgeClassName={starBadgeClassName}
          waveClassName={waveClassName}
          blurSrc={blurSrc}
          blurClassName={blurClassName}
          glowClassName={glowClassName}
          imageContainerBase={imageContainerBase}
          isHomeCard={isHomeCard}
          imageContainerClassName={imageContainerClassName}
          finalImageSrc={finalImageSrc}
          name={name}
          imageClassName={imageClassName}
          defaultImageClass={defaultImageClass}
          hidePanel={hidePanel}
          panelClassName={panelClassName}
          panelVariants={panelVariants}
          titleClassName={titleClassName}
          category={category}
          echoCount={echoCount}
          actionLabel={actionLabel}
          onAction={onAction}
        />
        {showTextOverlay && <TextOverlayLayer echo={echo} onAction={onAction} onClick={onClick} />}
      </motion.div>

      {!isCreate && !hidePanel && (
        <div className='mt-1 flex flex-col items-center px-1 text-center md:hidden'>
          <h3 className='w-full truncate font-proxima text-[22px] font-bold leading-tight tracking-normal text-white'>
            {name}
          </h3>
          <p className='mt-0 w-full truncate font-sans text-[12px] font-normal leading-tight tracking-normal text-gray-300'>
            {category}
          </p>
        </div>
      )}
    </div>
  );
};

export default EchoCard;
